import {
  type HitArea,
  type Point,
  type Polygon,
  type QuadraticBezierCurve,
  point,
  polygon,
  quadraticBezierCurve,
  rectangle,
  segment,
  vector,
} from '../elements';
import type { NonZeroSegment } from '../elements/segment';
import { EDGE_HIT_AREA_THICKNESS } from '../graphConstants';
import type { Radians } from '../utils/angles';
import { squared } from '../utils/numbers';
import {
  type EdgePositions,
  type EdgePositionsLine,
  getEdgeArrowHeadsList,
  isEdgeLinePoint,
} from './EdgePositions';
import { getEdgeConvexHullPoints } from './positions';

function analyzeLine(line: EdgePositionsLine): {
  segments: NonZeroSegment[];
  beziers: QuadraticBezierCurve[];
  edgeAngle: Radians;
} {
  const firstPoint: Point = line[0];
  let lastPoint: Point = firstPoint;
  // transform line to list of segments and beziers
  const segments: NonZeroSegment[] = [];
  const beziers: QuadraticBezierCurve[] = [];
  for (let i = 1; i < line.length; i += 1) {
    const current = line[i];
    if (current === undefined) {
      throw new Error(
        `Unexpected error, line is missing points: ${JSON.stringify(line)}`,
      );
    }

    if (isEdgeLinePoint(current)) {
      const seg = segment.create(lastPoint, current);
      if (segment.isNonZero(seg)) {
        segments.push(seg);
      }
    } else {
      beziers.push(quadraticBezierCurve.fromLinePart(current, lastPoint));
    }
    lastPoint = isEdgeLinePoint(current) ? current : current.e;
  }

  const edgeVector = vector.fromAtoB(firstPoint, lastPoint);
  const edgeAngle = vector.isNonZero(edgeVector)
    ? vector.getAngle(edgeVector)
    : 0;
  return { segments, beziers, edgeAngle };
}

function getRoughHitAreaRotated(
  positions: EdgePositions,
  edgeAngle: Radians,
  thickness: number,
) {
  // Approximate the hitarea with a rectangle. We use a non-orthogonal rectangle
  // rotated to edge's direction, since most edges are +/- long lines from
  // one point to other and their hitarea approximately fits such rotated
  // rectangle.
  const controlPoints = getEdgeConvexHullPoints(positions).map((p) =>
    vector.rotateBy(p, -edgeAngle),
  );
  const boundingBoxRotated = rectangle.createPointsBoundingBox(
    ...controlPoints,
  );
  const halfThickness = (EDGE_HIT_AREA_THICKNESS + thickness) / 2;
  return rectangle.createPointsBoundingBox(
    {
      x: boundingBoxRotated.minX - halfThickness,
      y: boundingBoxRotated.minY - halfThickness,
    },
    {
      x: boundingBoxRotated.maxX + halfThickness,
      y: boundingBoxRotated.maxY + halfThickness,
    },
  );
}

/**
 * Create HitArea of an edge line.
 *
 * Hitarea of an edge is any point with distance from the edge than
 * half of edge thickness.
 *
 * To gain best performance, HitArea created by this function first uses
 * a rough bounding box. It triggers exact determination whether the point
 * is in edge's HitArea only for points in this box.
 */
export function createEdgeHitArea(
  positions: EdgePositions,
  thickness: number,
): HitArea {
  const { segments, beziers, edgeAngle } = analyzeLine(positions.line);

  const roughHitAreaRotated = getRoughHitAreaRotated(
    positions,
    edgeAngle,
    thickness,
  );

  const segmentSquaredDistanceGetters = segments.map((s) =>
    segment.makeGetSquaredDistance(s),
  );

  const halfThickness = (EDGE_HIT_AREA_THICKNESS + thickness) / 2;
  const halfThicknessSquared = halfThickness ** 2;

  const isSegmentHit =
    (p: Point) => (getSquaredDistance: (p: Point) => number) =>
      getSquaredDistance(p) <= halfThicknessSquared;
  const isBezierHit = (p: Point) => (q: QuadraticBezierCurve) =>
    quadraticBezierCurve.getSquaredDistance(q, p) <= halfThicknessSquared;
  const { circle } = positions;
  const isCircleHit = circle
    ? (p: Point) =>
        point.getSquaredDistance(p, circle.position) <= squared(circle.radius)
    : () => false;
  const isPolygonHit = (pnt: Point) => (pg: Polygon) =>
    polygon.contains(pg, pnt);

  const isRoughHit = (p: Point) =>
    roughHitAreaRotated.containsPoint(vector.rotateBy(p, -edgeAngle));
  const isExactHit = (p: Point) =>
    segmentSquaredDistanceGetters.some(isSegmentHit(p)) ||
    beziers.some(isBezierHit(p)) ||
    isCircleHit(p) ||
    getEdgeArrowHeadsList(positions).some(isPolygonHit(p));
  return {
    // first check the rotated bounding box rectangle approximation.
    // If it is hit, try to find an element of the line
    // that is closer than half of edge thickness to the point.
    contains: (x: number, y: number) =>
      isRoughHit({ x, y }) && isExactHit({ x, y }),
  };
}
